import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Calendar, Clock, Users, Send, Loader2, Video, CheckCircle,
  AlertCircle, Mic, RefreshCw, ExternalLink, X, ChevronRight,
  FileText, UserCircle, Sparkles, Zap, Edit3, Save, Plus, Trash2,
  LayoutGrid, List, Search, Filter, ChevronDown, CalendarDays
} from 'lucide-react';
import { MeetingService, BotStatus, getStatusLabel, getStatusColor } from '../services/MeetingService';
import { deductSubscriptionCredits } from '../api/billing';
import ConfirmDeleteModal from './ConfirmDeleteModal';

const ASSISTANT_CREDIT_COST = 500;

// Meeting type detection helper
const getMeetingType = (session) => {
  try {
    const url = new URL(session.meeting_url || '');
    if (url.hostname.includes('zoom')) return 'zoom';
    if (url.hostname.includes('meet.google')) return 'google_meet';
    if (url.hostname.includes('teams')) return 'teams';
  } catch { }
  return 'other';
};

// Get meeting title from session or URL
const getMeetingTitle = (session) => {
  if (session.title) return session.title;

  try {
    const url = new URL(session.meeting_url || '');
    if (url.hostname.includes('zoom')) return 'Zoom Meeting';
    if (url.hostname.includes('meet.google')) return 'Google Meet';
    if (url.hostname.includes('teams')) return 'Teams Meeting';
  } catch { }

  return 'Meeting Session';
};

const MEETING_TYPES = [
  { id: 'all', label: 'All Types', icon: Video },
  { id: 'google_meet', label: 'Google Meet', icon: Video },
  { id: 'zoom', label: 'Zoom', icon: Video },
  { id: 'teams', label: 'Teams', icon: Video },
  { id: 'other', label: 'Other', icon: Video }
];

const MeetingAssistant = ({ userCredits = 0, onRefreshCredits, onTopUp, creditPacks = [], isPaymentProcessing = false }) => {
  const [meetingUrl, setMeetingUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [error, setError] = useState(null);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [pendingMeetingUrl, setPendingMeetingUrl] = useState('');
  const [modalTab, setModalTab] = useState('confirm'); // 'confirm' | 'topup'
  const [pendingTopUp, setPendingTopUp] = useState(null);
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState('');
  const [editedSummary, setEditedSummary] = useState('');
  const [editedActionItems, setEditedActionItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Delete state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // View and filter state
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [meetingTypeFilter, setMeetingTypeFilter] = useState('all');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showDatePicker, setShowDatePicker] = useState(false);

  const eventSourceRef = useRef(null);
  const pollCleanupRef = useRef(null);
  const sseReconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3;
  const typeDropdownRef = useRef(null);
  const datePickerRef = useRef(null);

  const canAfford = userCredits >= ASSISTANT_CREDIT_COST;

  // Credit pack options
  const creditPackOptions = creditPacks.length
    ? creditPacks.map((pack) => ({
      credits: Number(String(pack.credits).replace(/[^0-9]/g, "")),
      label: `Buy ${Number(String(pack.credits).replace(/[^0-9]/g, "")).toLocaleString()} Credits - ${pack.price || ""}`.trim(),
      sub: pack.discountPercentage ? `Save ${pack.discountPercentage}%` : "Instant Access.",
      featured: Boolean(pack.featured),
      lookupKey: pack.stripeLookupKey
    }))
    : [
      { credits: 2000, label: "Buy 2,000 Credits - $20", sub: "Instant Access.", lookupKey: "credits_2000" },
      { credits: 6000, label: "Buy 6,000 Credits - $50", sub: "(Best Seller)", featured: true, lookupKey: "credits_6000" },
      { credits: 30000, label: "Buy 30,000 Credits - $200", sub: "Save 33%.", lookupKey: "credits_30000" },
    ];

  // Filter and sort sessions
  const filteredSessions = useMemo(() => {
    let result = [...sessions];

    // Filter by search query (title, summary)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(session => {
        const title = (session.title || getMeetingTitle(session)).toLowerCase();
        const summary = (session.summary || '').toLowerCase();
        return title.includes(query) || summary.includes(query);
      });
    }

    // Filter by meeting type
    if (meetingTypeFilter !== 'all') {
      result = result.filter(session => getMeetingType(session) === meetingTypeFilter);
    }

    // Filter by date range
    if (dateRange.start) {
      const startDate = new Date(dateRange.start);
      startDate.setHours(0, 0, 0, 0);
      result = result.filter(session => {
        const sessionDate = new Date(session.created_at);
        return sessionDate >= startDate;
      });
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end);
      endDate.setHours(23, 59, 59, 999);
      result = result.filter(session => {
        const sessionDate = new Date(session.created_at);
        return sessionDate <= endDate;
      });
    }

    // Sort by most recent (already sorted from backend, but ensure consistency)
    result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    return result;
  }, [sessions, searchQuery, meetingTypeFilter, dateRange]);

  // Click outside handlers for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear filters helper
  const clearFilters = () => {
    setSearchQuery('');
    setMeetingTypeFilter('all');
    setDateRange({ start: '', end: '' });
  };

  const hasActiveFilters = searchQuery || meetingTypeFilter !== 'all' || dateRange.start || dateRange.end;

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions();
    // Only connect to SSE after sessions load successfully
    // This prevents spamming connection attempts when backend is down

    return () => {
      // Cleanup SSE connection
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      // Cleanup polling
      if (pollCleanupRef.current) {
        pollCleanupRef.current();
      }
    };
  }, []);

  const fetchSessions = async () => {
    try {
      setIsFetching(true);
      setError(null);
      const data = await MeetingService.getSessions(50);
      setSessions(Array.isArray(data) ? data : []);
      // Connect to SSE only after successful fetch (backend is available)
      connectToSSE();
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      // Don't show error on initial load if backend just isn't ready yet
      // User can still use the "Send Notetaker" feature
      if (err.message?.includes('401') || err.message?.includes('403')) {
        setError('Authentication required. Please log in again.');
      } else {
        // Backend might not be running - show helpful message
        setError('Meeting service unavailable. You can still invite a notetaker to new meetings.');
      }
    } finally {
      setIsFetching(false);
    }
  };

  const connectToSSE = useCallback(() => {
    // Don't reconnect if we've exceeded max attempts
    if (sseReconnectAttempts.current >= maxReconnectAttempts) {
      console.log('SSE: Max reconnect attempts reached, falling back to manual refresh');
      return;
    }

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    eventSourceRef.current = MeetingService.connectToEvents(
      (data) => {
        // Reset reconnect attempts on successful message
        sseReconnectAttempts.current = 0;
        console.log('SSE message:', data);

        if (data.type === 'status_update' && data.session_id) {
          // Update the session in our list
          setSessions(prev => prev.map(session => {
            if (session.id === data.session_id) {
              return {
                ...session,
                status: data.status,
                summary: data.summary || session.summary,
                action_items: data.action_items || session.action_items,
                updated_at: new Date().toISOString()
              };
            }
            return session;
          }));

          // Update selected session if it's the one being updated
          setSelectedSession(prev => {
            if (prev && prev.id === data.session_id) {
              return {
                ...prev,
                status: data.status,
                summary: data.summary || prev.summary,
                action_items: data.action_items || prev.action_items
              };
            }
            return prev;
          });
        }
      },
      (error) => {
        // Only log once per attempt cycle
        if (sseReconnectAttempts.current < maxReconnectAttempts) {
          sseReconnectAttempts.current += 1;
          console.log(`SSE connection error (attempt ${sseReconnectAttempts.current}/${maxReconnectAttempts})`);
          // Reconnect with exponential backoff
          const delay = Math.min(5000 * sseReconnectAttempts.current, 30000);
          setTimeout(connectToSSE, delay);
        }
      }
    );
  }, []);

  // Opens the credit confirmation modal
  const handleSendNotetaker = () => {
    if (!meetingUrl.trim()) return;

    // Basic URL validation
    try {
      new URL(meetingUrl);
    } catch {
      setError('Please enter a valid meeting URL (Zoom, Google Meet, or Teams)');
      return;
    }

    // Store the URL and show credit confirmation modal
    setPendingMeetingUrl(meetingUrl);
    setShowCreditModal(true);
  };

  // Actually sends the assistant after credit confirmation
  const handleConfirmSendAssistant = async () => {
    if (!pendingMeetingUrl.trim()) return;

    setShowCreditModal(false);
    setIsLoading(true);
    setError(null);

    try {
      const response = await MeetingService.inviteBot(pendingMeetingUrl);

      // Add new session to the list
      const newSession = {
        id: response.session_id,
        bot_id: response.bot_id,
        meeting_url: response.meeting_url,
        status: response.status || BotStatus.JOINING,
        created_at: response.created_at || new Date().toISOString(),
        summary: null,
        action_items: []
      };

      setSessions(prev => [newSession, ...prev]);
      setMeetingUrl('');
      setPendingMeetingUrl('');

      // Deduct credits after successful bot invite
      try {
        await deductSubscriptionCredits(ASSISTANT_CREDIT_COST);
      } catch (creditError) {
        console.error('Failed to deduct credits:', creditError);
        // Don't block the flow - bot was already invited successfully
      }

      // Refresh credits display to show updated balance
      if (onRefreshCredits) {
        onRefreshCredits();
      }

      // Start polling as fallback in case SSE doesn't work
      if (pollCleanupRef.current) {
        pollCleanupRef.current();
      }
      pollCleanupRef.current = MeetingService.pollForResults(
        response.session_id,
        (results) => {
          setSessions(prev => prev.map(s =>
            s.id === response.session_id ? { ...s, ...results } : s
          ));
        }
      );

    } catch (err) {
      console.error('Failed to invite bot:', err);
      setError(err.message || 'Failed to send notetaker. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelCreditModal = () => {
    setShowCreditModal(false);
    setPendingMeetingUrl('');
    setModalTab('confirm');
    setPendingTopUp(null);
  };

  // Edit mode handlers
  const handleStartEdit = () => {
    if (!selectedSession) return;
    setEditedTitle(selectedSession.title || getMeetingTitle(selectedSession));
    setEditedSummary(selectedSession.summary || '');
    // Deep clone action items for editing
    setEditedActionItems(
      selectedSession.action_items?.map(userItems => ({
        user: userItems.user || 'Unknown',
        action_items: [...(userItems.action_items || [])]
      })) || []
    );
    setSaveError(null);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedTitle('');
    setEditedSummary('');
    setEditedActionItems([]);
    setSaveError(null);
  };

  const handleSaveEdit = async () => {
    if (!selectedSession) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      const updates = {
        title: editedTitle,
        summary: editedSummary,
        action_items: editedActionItems
      };

      const updatedSession = await MeetingService.updateSession(selectedSession.id, updates);

      // Update the session in state
      const newSessionData = {
        ...selectedSession,
        title: editedTitle,
        summary: editedSummary,
        action_items: editedActionItems,
        ...updatedSession
      };

      setSelectedSession(newSessionData);
      setSessions(prev => prev.map(s =>
        s.id === selectedSession.id ? newSessionData : s
      ));

      setIsEditing(false);
    } catch (err) {
      console.error('Failed to save meeting notes:', err);
      setSaveError(err.message || 'Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Action item editing helpers
  const handleAddActionItem = (userIndex) => {
    setEditedActionItems(prev => {
      const updated = [...prev];
      updated[userIndex] = {
        ...updated[userIndex],
        action_items: [...(updated[userIndex].action_items || []), '']
      };
      return updated;
    });
  };

  const handleUpdateActionItem = (userIndex, itemIndex, value) => {
    setEditedActionItems(prev => {
      const updated = [...prev];
      updated[userIndex] = {
        ...updated[userIndex],
        action_items: updated[userIndex].action_items.map((item, i) =>
          i === itemIndex ? value : item
        )
      };
      return updated;
    });
  };

  const handleDeleteActionItem = (userIndex, itemIndex) => {
    setEditedActionItems(prev => {
      const updated = [...prev];
      updated[userIndex] = {
        ...updated[userIndex],
        action_items: updated[userIndex].action_items.filter((_, i) => i !== itemIndex)
      };
      return updated;
    });
  };

  const handleAddUser = () => {
    setEditedActionItems(prev => [
      ...prev,
      { user: 'New Assignee', action_items: [''] }
    ]);
  };

  const handleUpdateUserName = (userIndex, name) => {
    setEditedActionItems(prev => {
      const updated = [...prev];
      updated[userIndex] = { ...updated[userIndex], user: name };
      return updated;
    });
  };

  const handleDeleteUser = (userIndex) => {
    setEditedActionItems(prev => prev.filter((_, i) => i !== userIndex));
  };

  // Delete handlers
  const handleDeleteClick = (e, session) => {
    e.stopPropagation(); // Prevent card click from opening detail modal
    setSessionToDelete(session);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;

    setIsDeleting(true);
    try {
      await MeetingService.deleteSession(sessionToDelete.id);

      // Remove from sessions list
      setSessions(prev => prev.filter(s => s.id !== sessionToDelete.id));

      // Close detail modal if deleting the currently selected session
      if (selectedSession?.id === sessionToDelete.id) {
        setSelectedSession(null);
      }

      setShowDeleteModal(false);
      setSessionToDelete(null);
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError(err.message || 'Failed to delete meeting. Please try again.');
      setShowDeleteModal(false);
      setSessionToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setSessionToDelete(null);
  };

  const handleRefresh = () => {
    // Reset SSE reconnect attempts so user can try again
    sseReconnectAttempts.current = 0;
    fetchSessions();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const calculateDuration = (createdAt, updatedAt) => {
    if (!createdAt) return '-';
    const start = new Date(createdAt);
    const end = updatedAt ? new Date(updatedAt) : new Date();
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 1) return '< 1 min';
    if (diffMins < 60) return `${diffMins} min`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const getStatusBadge = (status) => {
    const color = getStatusColor(status);
    const label = getStatusLabel(status);

    const colorClasses = {
      emerald: 'text-emerald-700 bg-emerald-50 dark:text-emerald-400 dark:bg-emerald-900/30',
      amber: 'text-yellow-700 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/30',
      red: 'text-red-700 bg-red-50 dark:text-red-400 dark:bg-red-900/30',
      sky: 'text-yellow-700 bg-yellow-50 dark:text-yellow-400 dark:bg-yellow-900/30',
      slate: 'text-slate-700 bg-slate-50 dark:text-slate-400 dark:bg-slate-900/30'
    };

    const icons = {
      [BotStatus.DONE]: <CheckCircle size={12} />,
      [BotStatus.ERROR]: <AlertCircle size={12} />,
      [BotStatus.IN_CALL]: <Loader2 size={12} className="animate-spin" />,
      [BotStatus.PROCESSING]: <Loader2 size={12} className="animate-spin" />,
      [BotStatus.JOINING]: <Loader2 size={12} className="animate-spin" />,
      [BotStatus.IN_WAITING_ROOM]: <Clock size={12} />,
      [BotStatus.CALL_ENDED]: <CheckCircle size={12} />
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${colorClasses[color]}`}>
        {icons[status] || null}
        {label}
      </span>
    );
  };

  const handleSessionClick = async (session) => {
    setSelectedSession(session);

    // Fetch latest details if the session might have updates
    if (session.status !== BotStatus.DONE && session.status !== BotStatus.ERROR) {
      try {
        const details = await MeetingService.getSession(session.id);
        setSelectedSession(details);
      } catch (err) {
        console.error('Failed to fetch session details:', err);
      }
    }
  };

  return (
    <div className="flex-1 overflow-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
              Meeting Assistant
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Send Cloud Mortgage's assistant to your meetings and get AI-powered summaries and action items
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isFetching}
            className="p-2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={18} className={isFetching ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search meetings..."
              className="w-full pl-10 pr-4 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Meeting Type Filter */}
          <div className="relative" ref={typeDropdownRef}>
            <button
              onClick={() => setShowTypeDropdown(!showTypeDropdown)}
              className="flex items-center gap-2 px-4 py-2.5 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 transition-colors min-w-[140px]"
            >
              <Filter size={16} />
              <span className="flex-1 text-left">
                {MEETING_TYPES.find(t => t.id === meetingTypeFilter)?.label || 'All Types'}
              </span>
              <ChevronDown size={16} className={`transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showTypeDropdown && (
              <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20 py-1">
                {MEETING_TYPES.map((type) => (
                  <button
                    key={type.id}
                    onClick={() => {
                      setMeetingTypeFilter(type.id);
                      setShowTypeDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 transition-colors ${meetingTypeFilter === type.id
                      ? 'bg-yellow-50 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                  >
                    <type.icon size={16} />
                    {type.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Date Range Filter */}
          <div className="relative" ref={datePickerRef}>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm border rounded-lg transition-colors min-w-[140px] ${dateRange.start || dateRange.end
                ? 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800 text-yellow-600 dark:text-yellow-400'
                : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                }`}
            >
              <CalendarDays size={16} />
              <span className="flex-1 text-left">
                {dateRange.start || dateRange.end ? 'Date Filter' : 'All Dates'}
              </span>
              <ChevronDown size={16} className={`transition-transform ${showDatePicker ? 'rotate-180' : ''}`} />
            </button>

            {showDatePicker && (
              <div className="absolute top-full right-0 mt-1 w-72 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-xl z-20 p-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">From</label>
                    <input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">To</label>
                    <input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                      className="w-full px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-slate-900 dark:text-white"
                    />
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => setDateRange({ start: '', end: '' })}
                      className="flex-1 px-3 py-1.5 text-xs text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => setShowDatePicker(false)}
                      className="flex-1 px-3 py-1.5 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* View Toggle */}
          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${viewMode === 'grid'
                ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              title="Grid view"
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded transition-colors ${viewMode === 'list'
                ? 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-600 dark:text-yellow-400'
                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'
                }`}
              title="List view"
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Active Filters Indicator */}
        {hasActiveFilters && (
          <div className="flex items-center gap-2 mt-3">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Showing {filteredSessions.length} of {sessions.length} meetings
            </span>
            <button
              onClick={clearFilters}
              className="text-xs text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300 flex items-center gap-1"
            >
              <X size={12} />
              Clear filters
            </button>
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
          <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
            <X size={16} />
          </button>
        </div>
      )}

      {/* New Meeting Input (Always visible at top) */}
      <div className="mb-6 bg-white dark:bg-slate-800 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 p-4">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="flex items-center gap-3 flex-shrink-0">
            <div className="w-10 h-10 rounded-full bg-yellow-50 dark:bg-yellow-900/30 flex items-center justify-center">
              <Video className="w-5 h-5 text-yellow-500" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-slate-900 dark:text-white text-sm">
                New Meeting
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Paste a Zoom, Meet, or Teams URL
              </p>
            </div>
          </div>

          <div className="flex-1 flex gap-2 w-full sm:w-auto">
            <input
              type="text"
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSendNotetaker()}
              placeholder="Meeting URL"
              className="flex-1 px-4 py-2.5 text-sm bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-slate-900 dark:text-white placeholder-slate-400"
            />

            <button
              onClick={handleSendNotetaker}
              disabled={isLoading || !meetingUrl.trim()}
              className="px-6 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Send Assistant
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Loading Skeleton */}
      {isFetching && sessions.length === 0 && (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4' : 'space-y-3'}>
          {[1, 2, 3, 4].map(i => (
            viewMode === 'grid' ? (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 min-h-[200px] animate-pulse">
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-3" />
                <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-1/4 mb-4" />
                <div className="space-y-2 flex-1">
                  <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-full" />
                  <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-full" />
                  <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-2/3" />
                </div>
              </div>
            ) : (
              <div key={i} className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-lg flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-2" />
                    <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded w-2/3" />
                  </div>
                  <div className="h-6 bg-slate-100 dark:bg-slate-800 rounded w-20" />
                </div>
              </div>
            )
          ))}
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && !isFetching && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              onClick={() => handleSessionClick(session)}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 cursor-pointer hover:shadow-lg hover:border-yellow-300 dark:hover:border-yellow-600 transition-all min-h-[200px] flex flex-col"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getMeetingType(session) === 'google_meet' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                    getMeetingType(session) === 'zoom' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                      getMeetingType(session) === 'teams' ? 'bg-purple-100 dark:bg-purple-900/30' :
                        'bg-slate-100 dark:bg-slate-700'
                    }`}>
                    <Video size={16} className={
                      getMeetingType(session) === 'google_meet' ? 'text-yellow-500' :
                        getMeetingType(session) === 'zoom' ? 'text-yellow-500' :
                          getMeetingType(session) === 'teams' ? 'text-purple-500' :
                            'text-slate-500'
                    } />
                  </div>
                  <h3 className="font-semibold text-slate-900 dark:text-white line-clamp-2 flex-1 text-sm">
                    {getMeetingTitle(session)}
                  </h3>
                </div>
                {getStatusBadge(session.status)}
              </div>

              {/* Summary or Status Message */}
              <div className="flex-1 mb-3">
                {session.summary ? (
                  <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-3">
                    {session.summary}
                  </p>
                ) : session.status === BotStatus.DONE ? (
                  <p className="text-sm text-slate-500 dark:text-slate-500 italic">
                    No summary available
                  </p>
                ) : session.status === BotStatus.ERROR ? (
                  <p className="text-sm text-red-500">
                    Failed to process meeting
                  </p>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
                    <Loader2 size={14} className="animate-spin" />
                    <span>{getStatusLabel(session.status)}</span>
                  </div>
                )}
              </div>

              {/* Action Items Preview */}
              {session.action_items && session.action_items.length > 0 && (
                <div className="mb-3 pb-3 border-b border-slate-100 dark:border-slate-700">
                  <div className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                    <CheckCircle size={12} />
                    <span>{session.action_items.reduce((acc, u) => acc + (u.action_items?.length || 0), 0)} action items</span>
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 pt-3 border-t border-slate-100 dark:border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    <Calendar size={14} />
                    <span>{formatDate(session.created_at)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span>{calculateDuration(session.created_at, session.updated_at)}</span>
                  </div>
                </div>
                <button
                  onClick={(e) => handleDeleteClick(e, session)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  title="Delete meeting"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && !isFetching && (
        <div className="space-y-2">
          {/* List Header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-4 py-2 text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            <div className="col-span-4">Meeting</div>
            <div className="col-span-3">Summary</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-1">Duration</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {filteredSessions.map((session) => (
            <div
              key={session.id}
              onClick={() => handleSessionClick(session)}
              className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4 cursor-pointer hover:shadow-md hover:border-yellow-300 dark:hover:border-yellow-600 transition-all"
            >
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-4 items-center">
                {/* Meeting Info */}
                <div className="sm:col-span-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getMeetingType(session) === 'google_meet' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                    getMeetingType(session) === 'zoom' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                      getMeetingType(session) === 'teams' ? 'bg-purple-100 dark:bg-purple-900/30' :
                        'bg-slate-100 dark:bg-slate-700'
                    }`}>
                    <Video size={18} className={
                      getMeetingType(session) === 'google_meet' ? 'text-yellow-500' :
                        getMeetingType(session) === 'zoom' ? 'text-yellow-500' :
                          getMeetingType(session) === 'teams' ? 'text-purple-500' :
                            'text-slate-500'
                    } />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-slate-900 dark:text-white text-sm truncate">
                      {getMeetingTitle(session)}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
                      {getMeetingType(session).replace('_', ' ')}
                    </p>
                  </div>
                </div>

                {/* Summary */}
                <div className="sm:col-span-3">
                  {session.summary ? (
                    <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
                      {session.summary}
                    </p>
                  ) : session.status === BotStatus.DONE ? (
                    <span className="text-sm text-slate-400 dark:text-slate-500 italic">No summary</span>
                  ) : session.status === BotStatus.ERROR ? (
                    <span className="text-sm text-red-500">Failed</span>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 size={12} className="animate-spin" />
                      <span>{getStatusLabel(session.status)}</span>
                    </div>
                  )}
                </div>

                {/* Date */}
                <div className="sm:col-span-2 flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                  <Calendar size={14} className="text-slate-400 flex-shrink-0 hidden sm:block" />
                  <span>{formatDate(session.created_at)}</span>
                  <span className="text-slate-400">{formatTime(session.created_at)}</span>
                </div>

                {/* Duration */}
                <div className="sm:col-span-1 flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                  <Clock size={14} className="text-slate-400 flex-shrink-0 hidden sm:block" />
                  <span>{calculateDuration(session.created_at, session.updated_at)}</span>
                </div>

                {/* Status & Action Items */}
                <div className="sm:col-span-2 flex items-center justify-end gap-2">
                  {session.action_items && session.action_items.length > 0 && (
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-1 rounded-full flex items-center gap-1">
                      <CheckCircle size={12} />
                      {session.action_items.reduce((acc, u) => acc + (u.action_items?.length || 0), 0)}
                    </span>
                  )}
                  {getStatusBadge(session.status)}
                  <button
                    onClick={(e) => handleDeleteClick(e, session)}
                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete meeting"
                  >
                    <Trash2 size={14} />
                  </button>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isFetching && sessions.length === 0 && (
        <div className="text-center py-12">
          <Video className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
            No meetings yet
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Invite a notetaker to your first meeting to get started
          </p>
        </div>
      )}

      {/* No Results State (when filters active) */}
      {!isFetching && sessions.length > 0 && filteredSessions.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
            No meetings match your filters
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Try adjusting your search or filter criteria
          </p>
          <button
            onClick={clearFilters}
            className="text-sm text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Session Detail Modal */}
      {selectedSession && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => {
            if (!isEditing) {
              setSelectedSession(null);
              handleCancelEdit();
            }
          }}
        >
          <div
            className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value)}
                      className="w-full text-xl font-bold text-slate-900 dark:text-white bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                      placeholder="Meeting title"
                    />
                  ) : (
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                        {selectedSession.title || getMeetingTitle(selectedSession)}
                      </h2>
                      {getStatusBadge(selectedSession.status)}
                    </div>
                  )}
                  <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {formatDate(selectedSession.created_at)} at {formatTime(selectedSession.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {calculateDuration(selectedSession.created_at, selectedSession.updated_at)}
                    </span>
                  </div>
                  {selectedSession.meeting_url && !isEditing && (
                    <a
                      href={selectedSession.meeting_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 mt-2 text-xs text-yellow-500 hover:text-yellow-600"
                    >
                      <ExternalLink size={12} />
                      View original meeting
                    </a>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedSession(null);
                    handleCancelEdit();
                  }}
                  disabled={isSaving}
                  className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {/* Save Error */}
              {saveError && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mb-6">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700 dark:text-red-400">{saveError}</p>
                  </div>
                  <button onClick={() => setSaveError(null)} className="text-red-500 hover:text-red-700">
                    <X size={16} />
                  </button>
                </div>
              )}

              {/* Status Message for In-Progress */}
              {selectedSession.status !== BotStatus.DONE && selectedSession.status !== BotStatus.ERROR && (
                <div className="flex items-center gap-3 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg mb-6">
                  <Loader2 className="w-5 h-5 text-yellow-500 animate-spin" />
                  <div>
                    <p className="font-medium text-yellow-700 dark:text-yellow-400">
                      {getStatusLabel(selectedSession.status)}
                    </p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-500">
                      Results will appear here when the meeting ends
                    </p>
                  </div>
                </div>
              )}

              {/* Error State */}
              {selectedSession.status === BotStatus.ERROR && (
                <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg mb-6">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="font-medium text-red-700 dark:text-red-400">
                      Failed to process meeting
                    </p>
                    <p className="text-sm text-red-600 dark:text-red-500">
                      The bot encountered an error. Please try again.
                    </p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Summary Section */}
                {(isEditing || selectedSession.summary) && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <FileText size={16} className="text-yellow-500" />
                      Meeting Summary
                    </h3>
                    {isEditing ? (
                      <textarea
                        value={editedSummary}
                        onChange={(e) => setEditedSummary(e.target.value)}
                        rows={6}
                        className="w-full text-sm text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-yellow-500 resize-none"
                        placeholder="Enter meeting summary..."
                      />
                    ) : (
                      <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed bg-slate-50 dark:bg-slate-900 p-4 rounded-lg whitespace-pre-wrap">
                        {selectedSession.summary}
                      </p>
                    )}
                  </div>
                )}

                {/* Action Items Section */}
                {(isEditing || (selectedSession.action_items && selectedSession.action_items.length > 0)) && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2">
                      <CheckCircle size={16} className="text-emerald-500" />
                      Action Items
                    </h3>
                    <div className="space-y-4">
                      {isEditing ? (
                        <>
                          {editedActionItems.map((userItems, userIdx) => (
                            <div key={userIdx} className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <UserCircle size={18} className="text-slate-400" />
                                <input
                                  type="text"
                                  value={userItems.user}
                                  onChange={(e) => handleUpdateUserName(userIdx, e.target.value)}
                                  className="flex-1 font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                  placeholder="Assignee name"
                                />
                                <button
                                  onClick={() => handleDeleteUser(userIdx)}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                  title="Remove assignee"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                              <div className="space-y-2 pl-6">
                                {userItems.action_items?.map((item, itemIdx) => (
                                  <div key={itemIdx} className="flex items-start gap-2">
                                    <span className="text-slate-400 mt-2">•</span>
                                    <input
                                      type="text"
                                      value={item}
                                      onChange={(e) => handleUpdateActionItem(userIdx, itemIdx, e.target.value)}
                                      className="flex-1 text-sm text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                                      placeholder="Action item..."
                                    />
                                    <button
                                      onClick={() => handleDeleteActionItem(userIdx, itemIdx)}
                                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                      title="Remove action item"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))}
                                <button
                                  onClick={() => handleAddActionItem(userIdx)}
                                  className="flex items-center gap-1 text-xs text-yellow-500 hover:text-yellow-600 mt-2"
                                >
                                  <Plus size={14} />
                                  Add action item
                                </button>
                              </div>
                            </div>
                          ))}
                          <button
                            onClick={handleAddUser}
                            className="w-full py-3 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600 transition-colors flex items-center justify-center gap-2"
                          >
                            <Plus size={16} />
                            Add assignee
                          </button>
                        </>
                      ) : (
                        selectedSession.action_items.map((userItems, idx) => (
                          <div key={idx} className="bg-slate-50 dark:bg-slate-900 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <UserCircle size={18} className="text-slate-400" />
                              <span className="font-medium text-slate-700 dark:text-slate-300">
                                {userItems.user || 'Unknown'}
                              </span>
                            </div>
                            <div className="space-y-2 pl-6">
                              {userItems.action_items?.map((item, itemIdx) => (
                                <div key={itemIdx} className="flex items-start gap-3">
                                  <input
                                    type="checkbox"
                                    className="mt-1 rounded border-slate-300 dark:border-slate-600 text-emerald-500 focus:ring-emerald-500"
                                  />
                                  <span className="text-sm text-slate-700 dark:text-slate-300">
                                    {item}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* No Content State */}
                {!isEditing && selectedSession.status === BotStatus.DONE &&
                  !selectedSession.summary &&
                  (!selectedSession.action_items || selectedSession.action_items.length === 0) && (
                    <div className="text-center py-8">
                      <Mic className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                      <p className="text-slate-500 dark:text-slate-400 mb-4">
                        No summary or action items were extracted from this meeting
                      </p>
                      <button
                        onClick={handleStartEdit}
                        className="text-sm text-yellow-500 hover:text-yellow-600 flex items-center gap-1 mx-auto"
                      >
                        <Edit3 size={14} />
                        Add notes manually
                      </button>
                    </div>
                  )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-emerald-500 hover:bg-emerald-600 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Changes
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => {
                      setSelectedSession(null);
                      handleCancelEdit();
                    }}
                    className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={(e) => handleDeleteClick(e, selectedSession)}
                    className="px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    Delete
                  </button>
                  {selectedSession.status === BotStatus.DONE && (
                    <>
                      <button
                        onClick={handleStartEdit}
                        className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-2"
                      >
                        <Edit3 size={16} />
                        Edit Notes
                      </button>
                      <button className="px-4 py-2 text-sm font-medium text-white bg-yellow-500 hover:bg-yellow-600 rounded-lg transition-colors flex items-center gap-2">
                        <FileText size={16} />
                        Export Notes
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Credit Confirmation Modal */}
      {showCreditModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center dark:bg-slate-900/80 bg-slate-500/50 backdrop-blur-sm px-4"
          onClick={handleCancelCreditModal}
        >
          <div
            className="dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-2xl max-w-lg w-full shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b dark:border-slate-800 border-slate-200 sticky top-0 dark:bg-slate-900 bg-white z-10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl dark:bg-emerald-500/20 bg-emerald-100 flex items-center justify-center">
                  <Sparkles className="w-5 h-5 dark:text-emerald-400 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold dark:text-white text-slate-900">Send A.I Assistant</h3>
                  <p className="text-sm text-slate-500">Confirm credit usage</p>
                </div>
              </div>
              <button
                onClick={handleCancelCreditModal}
                disabled={isPaymentProcessing}
                className="text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex items-center border-b dark:border-slate-700 border-slate-200 px-6">
              <button
                type="button"
                onClick={() => setModalTab('confirm')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-all border-b-2 ${modalTab === 'confirm'
                  ? 'border-yellow-500 dark:text-yellow-400 text-yellow-600'
                  : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
              >
                Send Assistant
              </button>
              <button
                type="button"
                onClick={() => setModalTab('topup')}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-all border-b-2 ${modalTab === 'topup'
                  ? 'border-emerald-500 dark:text-emerald-400 text-emerald-600'
                  : 'border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
                  }`}
              >
                Top Up Credits
              </button>
            </div>

            {/* Confirm Tab Content */}
            {modalTab === 'confirm' && (
              <div className="p-6 space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-xl dark:bg-slate-800/50 bg-slate-50 border dark:border-slate-700 border-slate-200">
                  <div className="w-12 h-12 rounded-xl dark:bg-yellow-500/20 bg-yellow-100 flex items-center justify-center flex-shrink-0">
                    <Video className="w-6 h-6 dark:text-yellow-400 text-yellow-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium dark:text-white text-slate-900">Meeting Assistant</p>
                    <p className="text-xs text-slate-500 truncate">{pendingMeetingUrl}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-500">AI Credits Required</span>
                    <span className="text-lg font-bold dark:text-yellow-400 text-yellow-600">{ASSISTANT_CREDIT_COST}</span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t dark:border-slate-700 border-slate-200">
                    <span className="text-sm text-slate-500">Your Balance</span>
                    <span className={`text-sm font-semibold ${canAfford ? 'dark:text-emerald-400 text-emerald-600' : 'dark:text-red-400 text-red-600'}`}>
                      {userCredits.toLocaleString()} credits
                    </span>
                  </div>
                  {!canAfford && (
                    <div className="flex items-center justify-between py-2 border-t dark:border-slate-700 border-slate-200">
                      <span className="text-sm text-slate-500">After This Action</span>
                      <span className="text-sm font-semibold dark:text-red-400 text-red-600">
                        Need {(ASSISTANT_CREDIT_COST - userCredits).toLocaleString()} more
                      </span>
                    </div>
                  )}
                  {canAfford && (
                    <div className="flex items-center justify-between py-2 border-t dark:border-slate-700 border-slate-200">
                      <span className="text-sm text-slate-500">After This Action</span>
                      <span className="text-sm font-semibold dark:text-slate-300 text-slate-700">
                        {(userCredits - ASSISTANT_CREDIT_COST).toLocaleString()} credits remaining
                      </span>
                    </div>
                  )}
                </div>

                {!canAfford && (
                  <div className="p-3 rounded-lg dark:bg-red-500/10 bg-red-50 border dark:border-red-500/30 border-red-200">
                    <p className="text-sm dark:text-red-300 text-red-700 flex items-center gap-2">
                      <AlertCircle size={16} />
                      Insufficient credits. Please top up to continue.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Top Up Tab Content */}
            {modalTab === 'topup' && (
              <div className="p-6">
                <div className="text-center mb-6">
                  <span className="inline-block px-3 py-1 dark:bg-emerald-500/10 bg-emerald-100 dark:text-emerald-400 text-emerald-700 text-xs font-semibold rounded-full mb-2">
                    Instant Credit Access
                  </span>
                  <h4 className="text-lg font-bold dark:text-white text-slate-900">Select a Credit Pack</h4>
                  <p className="text-sm text-slate-500 mt-1">Unlock AI-powered meeting notes.</p>
                </div>

                <div className="space-y-3">
                  {creditPackOptions.map((pack) => {
                    const isSelected = pendingTopUp?.credits === pack.credits;
                    return (
                      <button
                        key={pack.credits}
                        className={`relative w-full text-left rounded-xl p-4 transition-all duration-200 group ${isSelected
                          ? 'dark:bg-emerald-500/10 bg-emerald-50 border-2 border-emerald-500 shadow-[0_0_20px_rgba(16,185,129,0.2)]'
                          : 'dark:bg-slate-800/50 bg-white border dark:border-slate-700 border-slate-200 hover:border-emerald-500/50 dark:hover:bg-slate-800 hover:bg-slate-50'
                          }`}
                        onClick={() => setPendingTopUp(pack)}
                        disabled={isPaymentProcessing}
                      >
                        {pack.featured && (
                          <span className="absolute -top-2 right-4 bg-emerald-500 text-slate-900 text-[10px] font-bold px-2 py-0.5 rounded-full shadow-lg">
                            POPULAR
                          </span>
                        )}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className={`text-sm font-semibold ${isSelected ? 'dark:text-emerald-400 text-emerald-600' : 'dark:text-slate-200 text-slate-700 group-hover:text-emerald-600 dark:group-hover:text-emerald-400'}`}>
                              {Number(pack.credits).toLocaleString()} Credits
                            </p>
                            <p className="text-xs text-slate-500 mt-1">{pack.sub}</p>
                          </div>
                          <p className="text-lg font-bold dark:text-white text-slate-900">
                            {pack.label.split('-')[1]?.trim()}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex gap-3 p-6 border-t dark:border-slate-800 border-slate-200 sticky bottom-0 dark:bg-slate-900 bg-white z-10">
              <button
                onClick={handleCancelCreditModal}
                disabled={isPaymentProcessing}
                className="flex-1 px-4 py-2.5 rounded-xl border dark:border-slate-700 border-slate-200 dark:text-slate-300 text-slate-600 font-medium hover:bg-slate-100 dark:hover:bg-slate-800 dark:hover:text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>

              {modalTab === 'confirm' && (
                canAfford ? (
                  <button
                    onClick={handleConfirmSendAssistant}
                    disabled={isLoading}
                    className="flex-[2] px-4 py-2.5 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-500 transition-all shadow-lg shadow-emerald-900/20 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Zap size={18} />
                        Confirm • {ASSISTANT_CREDIT_COST} credits
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => setModalTab('topup')}
                    className="flex-[2] px-4 py-2.5 rounded-xl bg-yellow-600 text-white font-bold hover:bg-yellow-500 transition-all shadow-lg shadow-yellow-900/20 flex items-center justify-center gap-2"
                  >
                    <Sparkles size={18} />
                    Insufficient Credits - Buy More
                  </button>
                )
              )}

              {modalTab === 'topup' && (
                <button
                  onClick={() => pendingTopUp && onTopUp && onTopUp({ lookupKey: pendingTopUp.lookupKey, credits: pendingTopUp.credits })}
                  disabled={!pendingTopUp || isPaymentProcessing}
                  className={`flex-[2] px-4 py-2.5 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center gap-2 ${!pendingTopUp
                    ? 'dark:bg-slate-800 bg-slate-100 text-slate-500 cursor-not-allowed border dark:border-slate-700 border-slate-200'
                    : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-900/20'
                    }`}
                >
                  {isPaymentProcessing ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Processing...
                    </>
                  ) : pendingTopUp ? (
                    `Proceed to Checkout • ${pendingTopUp.label.split('- $')[1] || pendingTopUp.label}`
                  ) : (
                    'Select a Credit Pack'
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Meeting"
        message={`Are you sure you want to delete "${sessionToDelete ? (sessionToDelete.title || getMeetingTitle(sessionToDelete)) : 'this meeting'}"? This action cannot be undone.`}
        confirmText={isDeleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
      />
    </div>
  );
};

export default MeetingAssistant;
